<ol class="breadcrumb">
  <li class="breadcrumb-item"><a routerLink="/hospitals">Organizations</a></li>
  <li class="breadcrumb-item active">{{hospital.name}}</li>
</ol>

<h1>{{hospital.name}}</h1>

<tabset #tabset>
  <tab (select)="tabChanged($event)">
    <ng-template tabHeading>
      Details
    </ng-template>

    @if (!editingHospital) {
      <div>
        <table class="table table-striped">
          <tbody>
            <tr>
              <th class="width-250">ID</th>
              <td>{{hospital.id}}</td>
            </tr>
            <tr>
              <th>Name</th>
              <td>{{hospital.name}}</td>
            </tr>
            @if (hospital.customer_id) {
              <tr>
                <th>Customer</th>
                <td>
                  <a [routerLink]="['/customers', hospital.customer_id]">{{hospital.customer_name}}</a>
                </td>
              </tr>
            }
            @if (hospital.parent_id) {
              <tr>
                <th>Parent Organization</th>
                <td>
                  <a [routerLink]="['/hospitals', hospital.parent_id]">{{parentHospitalName}}</a>
                </td>
              </tr>
            }
            <tr>
              <th>Organization Type</th>
              <td>{{organizationTypesMap[hospital.organization_type]}}</td>
            </tr>
            <tr>
              <th>Created On</th>
              <td>{{hospital.created_at | date}}</td>
            </tr>
            <tr>
              <th>Updated On</th>
              <td>{{hospital.updated_at | date}}</td>
            </tr>
            <tr>
              <th>Advanced Notice for Ride Share Rides</th>
              <td>
                <span>{{hospital.advance_notice_for_ride_share_rides}} (minutes)</span>
                <p class="small">
                  The Advanced Notice setting for Ride Share rides lets us know how far in advance we should request a Ride Share ride for patients. For a large city we recommend the default time period of 5 minutes. For suburban and rural areas we recommend changing this to at least 15 minutes.
                </p>
              </td>
            </tr>
            <tr>
              <th>Client Specific Service Phone Number</th>
              <td>
                <span>{{hospital.service_phone_number}}</span>
              </td>
            </tr>
            <tr>
              <th>Payer Types</th>
              <td>
                @for (item of selectedPayerTypes; track item) {
                  <span>{{ item.itemName }}@if (!$last) {
                    <span>, </span>
                  }</span>
                }
              </td>
            </tr>
            <tr>
              <th>Password Expiration</th>
              <td>
                <span>{{hospital.password_expiration_interval}} {{ hospital.password_expiration_interval_type }}(s)</span>
              </td>
            </tr>
            <tr>
              <th>User Session Timeout</th>
              <td>
                @if (hospital.user_timeout_interval) {
                  <span>{{ hospital.user_timeout_interval }} (minutes)</span>
                }
                @if (!hospital.user_timeout_interval) {
                  <span>-</span>
                }
              </td>
            </tr>
            <tr>
              <th>Enabled Vehicles</th>
              <td>
                @for (item of selectedVehicles; track item) {
                  <span>{{ item.itemName }}@if (!$last) {
                    <span>, </span>
                  }</span>
                }
              </td>
            </tr>
            <tr>
              <th>Unit of Measurement</th>
              <td>{{measurementUnitMap[hospital.measurement_unit]}}</td>
            </tr>
            <tr>
              <th>
                Trip Reasons
                <a routerLink="/reason-types" class="reason-type-manage-link">
                  Manage
                </a>
              </th>
              <td>
                @for (item of selectedReasonTypes; track item) {
                  <span>{{ item.itemName }}@if (!$last) {
                    <span>, </span>
                  }</span>
                }
                <p class="small">
                  Trip reasons removed here might affect geofencing rules. Please check before continuing.
                </p>
              </td>
            </tr>
            <tr>
              <th>Email Offer Text</th>
              <td>
                <pre>{{hospital.email_offer_text}}</pre>
              </td>
            </tr>
            <tr>
              <th>Send Will Call Ready Text?</th>
              <td>
                <ui-switch id="will_call" name="will_call" [(ngModel)]="hospital.send_will_call_text" disabled="true">
                </ui-switch>
              </td>
            </tr>
            <tr>
              <th>Address</th>
              <td>{{hospital.address}}</td>
            </tr>
            <tr>
              <th>Tax ID</th>
              <td>{{hospital.tax_id}}</td>
            </tr>
            <tr>
              <th>Community Settings</th>
              <td>
                <p>Preferred Logic: <span>{{ ppStrategyDisplayNames[hospital.pp_strategy] }}</span></p>
                <p>Access Rules: <span>{{ accessStrategyDisplayNames[hospital.provider_access_strategy] }}</span></p>
              </td>
            </tr>
            <tr>
              <th>Disabled PCS?</th>
              <td>
                <ui-switch id="pcs_disabled" name="pcs_disabled" [(ngModel)]="hospital.pcs_disabled" disabled="true">
                </ui-switch>
              </td>
            </tr>
            @if (hospital.children.length == 0) {
              <tr>
                <th>Advanced Ride Cancelation?</th>
                <td>
                  <ui-switch id="advanced_cancelation_enabled" name="advanced_cancelation_enabled" [(ngModel)]="hospital.advanced_cancelation_enabled" disabled="true">
                  </ui-switch>
                </td>
              </tr>
            }
            @if (hospital.advanced_cancelation_enabled) {
              <tr>
                <th>Ride Cancel Threshold</th>
                <td>
                  {{hospital.advanced_cancelation_threshold}} hours
                </td>
              </tr>
            }
            @if (hospital.advanced_cancelation_enabled) {
              <tr>
                <th>Advanced Ride Cancelation Reason</th>
                <td>
                  {{hospital.advanced_cancelation_reason}}
                </td>
              </tr>
            }
            @if (hospital.children.length == 0) {
              <tr>
                <th>Advanced Calling Transport ZD Ticket</th>
                <td>
                  <ui-switch id="calling_providers_ticket_enabled" name="calling_providers_ticket_enabled" [(ngModel)]="hospital.calling_providers_ticket_enabled" disabled="true">
                  </ui-switch>
                </td>
              </tr>
            }
            @if (hospital.calling_providers_ticket_enabled && hospital.children.length == 0) {
              <tr>
                <th>Trigger before ride start time</th>
                <td>
                  {{hospital.calling_providers_ticket_threshold}} hours
                </td>
              </tr>
            }
            <tr>
              <th>
                Authorizations not by Roundtrip
                <small class="display-block">(Redox-integrated)</small>
              </th>
              <td>
                <ui-switch id="auth_disabled"
                  name="auth_disabled"
                  [(ngModel)]="hospital.auth_disabled"
                  disabled="true">
                </ui-switch>
              </td>
            </tr>
            <tr>
              <th>Allow Rider Creation</th>
              <td>
                <ui-switch id="allow_rider_creation"
                  name="allow_rider_creation"
                  [(ngModel)]="hospital.allow_rider_creation"
                  disabled="true">
                </ui-switch>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="text-left">
          <button class="btn btn-primary" (click)="editHospital()">Edit</button>
        </div>
      </div>
    }

    @if (editingHospital) {
      <div>
        <div class="row">
          <form class="form-horizontal col col-sm-6" [formGroup]="hospitalEditForm" >
            <div class="form-group">
              <label class="col-sm-5 control-label text-left">ID</label>
              <div class="col-sm-7">
                <input class="form-control" disabled type="text" formControlName="id">
              </div>
            </div>
            <div class="form-group">
              <label class="col-sm-5 control-label text-left">Name</label>
              <div class="col-sm-7">
                <input class="form-control" type="text" formControlName="name">
                @if (hospitalEditForm.get('name').invalid) {
                  <div class="form-error">
                    <p>Name cannot be empty.</p>
                  </div>
                }
              </div>
            </div>
            <ng-container>
              <div class="form-group">
                <label class="col-sm-5 control-label text-left">Customer</label>
                <div class="col-sm-7">
                  <select class="form-control" formControlName="customer_id">
                    <option [ngValue]="null">None</option>
                    @for (customer of customers; track customer) {
                      <option [value]="customer.id">
                        {{customer.name}}
                      </option>
                    }
                  </select>
                </div>
              </div>
            </ng-container>
            <ng-container>
              <div class="form-group">
                <label class="col-sm-5 control-label text-left">Parent Organization</label>
                <div class="col-sm-7">
                  <select class="form-control" formControlName="parent_id">
                    <option [ngValue]="null">None</option>
                    @for (hospital of parentHospitals; track hospital) {
                      <option [value]="hospital.id">
                        {{hospital.name}}
                      </option>
                    }
                  </select>
                </div>
              </div>
            </ng-container>
            <ng-container>
              <div class="form-group">
                <label class="col-sm-5 control-label text-left">Organization Type</label>
                <div class="col-sm-7">
                  <select class="form-control" formControlName="organization_type">
                    @for (type of organizationTypeOptions; track type) {
                      <option [value]="type.id">
                        {{type.name}}
                      </option>
                    }
                  </select>
                  @if (hospitalEditForm.get('organization_type') && hospitalEditForm.get('organization_type').invalid) {
                    <div class="form-error">
                      <p>Organization type cannot be empty.</p>
                    </div>
                  }
                </div>
              </div>
            </ng-container>
            <div class="form-group">
              <label class="col-sm-5 control-label text-left">Created On</label>
              <div class="col-sm-7">
                <input class="form-control" disabled type="text" formControlName="created_at">
              </div>
            </div>
            <div class="form-group">
              <label class="col-sm-5 control-label text-left">Updated On</label>
              <div class="col-sm-7">
                <input class="form-control" disabled type="text" formControlName="updated_at">
              </div>
            </div>
            <div class="form-group">
              <label class="col-sm-5 control-label text-left">Advanced Notice for Ride Share Rides</label>
              <div class="col-sm-7">
                <input class="form-control" type="number" formControlName="advance_notice_for_ride_share_rides">
                @if (hospitalEditForm.get('advance_notice_for_ride_share_rides').invalid) {
                  <div class="form-error">
                    <p>Notice period cannot be empty.</p>
                  </div>
                }
                <p class="small">
                  The Advanced Notice setting for Ride Share rides lets us know how far in advance we should request a Ride Share ride for patients. For a large city we recommend the default time period of 5 minutes. For suburban and rural areas we recommend changing this to at least 15 minutes.
                </p>
              </div>
            </div>
            <div class="form-group">
              <label class="col-sm-5 control-label text-left">Client Specific Service Phone Number</label>
              <div class="col-sm-7">
                <input class="form-control" type="text" formControlName="service_phone_number">
                @if (hospitalEditForm.get('service_phone_number').invalid) {
                  <div class="form-error">
                    <p>Service phone number cannot be empty.</p>
                  </div>
                }
              </div>
            </div>
            <div class="form-group">
              <label class="col-sm-5 control-label text-left">Payer Types</label>
              <div class="col-sm-7">
                <angular2-multiselect
                  formControlName="payer_types"
                  [data]="payerTypesList"
                  [settings]="payerTypeSettings"
                  (onDeSelect)="onPayerItemDeSelect($event)"
                  (onDeSelectAll)="onPayerDeSelectAll()"
                  >
                  <c-item>
                    <ng-template let-item="item">
                      <label [ngClass]="{'disabled-option': item['required']}">{{item.itemName}}</label>
                    </ng-template>
                  </c-item>
                </angular2-multiselect>
              </div>
            </div>
            <div class="form-group">
              <label class="col-sm-5 control-label text-left">Password Expiration</label>
              <div class="col-sm-7">
                <div class="row">
                  <div class="col col-sm-6">
                    <input class="form-control" type="number" placeholder="0 (in minutes)" formControlName="password_expiration_interval">
                  </div>
                  <div class="col col-sm-6">
                    <select class="form-control" formControlName="password_expiration_interval_type">
                      <option value="day">Day(s)</option>
                      <option value="week">Week(s)</option>
                      <option value="year">Year(s)</option>
                    </select>
                  </div>
                  @if (hospitalEditForm.get('password_expiration_interval').invalid ||
                    hospitalEditForm.get('password_expiration_interval_type').invalid) {
                    <div class="col col-sm-12 form-error">
                      <p>Invalid expiration period.</p>
                    </div>
                  }
                </div>
              </div>
            </div>
            <div class="form-group">
              <label class="col-sm-5 control-label text-left">User Session Timeout</label>
              <div class="col-sm-7">
                <input class="form-control" type="number" formControlName="user_timeout_interval">
                @if (hospitalEditForm.get('user_timeout_interval').invalid) {
                  <div class="form-error">
                    @if (hospitalEditForm.get('user_timeout_interval').errors?.min) {
                      <p>The value should be greater than or equal to 15.</p>
                    }
                    @if (hospitalEditForm.get('user_timeout_interval').errors?.max) {
                      <p>The value should be less than or equal to 480.</p>
                    }
                  </div>
                }
                <p class="small">
                  Please enter a value in minutes.
                </p>
              </div>
            </div>
            <div class="form-group">
              <label for="name" class="col-sm-5 control-label text-left">Enabled Vehicles</label>
              <div class="col-sm-7">
                <angular2-multiselect [data]="vehicleList" [(ngModel)]="selectedVehicles"
                  [settings]="vehicleDropDownSettings"
                  (onSelect)="onVehicleItemSelect($event)"
                  (onDeSelect)="OnVehicleItemDeSelect($event)"
                  formControlName="enabled_vehicles"
                  >
                </angular2-multiselect>
                @if (hospitalEditForm.get('enabled_vehicles').dirty && hospitalEditForm.get('enabled_vehicles').invalid) {
                  <div class="form-error">
                    <p>Please select at least one option.</p>
                  </div>
                }
              </div>
            </div>
            <div class="form-group">
              <label class="col-sm-5 control-label text-left">Unit of Measurement</label>
              <div class="col-sm-7">
                <select class="form-control" formControlName="measurement_unit">
                  @for (type of measurementUnitOptions; track type) {
                    <option [value]="type.id">
                      {{type.name}}
                    </option>
                  }
                </select>
              </div>
            </div>
            <div class="form-group">
              <label class="col-sm-5 control-label text-left">Trip Reasons</label>
              <div class="col-sm-7">
                <angular2-multiselect [data]="reasonTypesList" [(ngModel)]="selectedReasonTypes"
                  [settings]="reasonTypesDropDownSettings"
                  (onSelect)="onReasonItemSelect($event)"
                  (onDeSelect)="OnReasonItemDeSelect($event)"
                  formControlName="reason_types">
                </angular2-multiselect>
                @if (hospitalEditForm.get('reason_types').dirty && hospitalEditForm.get('reason_types').invalid) {
                  <div class="form-error">
                    <p>Please select at least one option.</p>
                  </div>
                }
                <p class="small">
                  Trip reasons removed here might affect geofencing rules. Please check before continuing.
                </p>
              </div>
            </div>
            <div class="form-group">
              <label class="col-sm-5 control-label text-left">Email Offer Text</label>
              <div class="col-sm-7">
                <textarea class="form-control" formControlName="email_offer_text" rows="5" placeholder="You are eligible for two free rides through the Facility program.">
                </textarea>
              </div>
            </div>
            <div class="form-group">
              <label class="col-sm-5 control-lable">Send Will Call Ready Text?</label>
              <div class="col-sm-7">
                <ui-switch id="will_call" name="will_call" formControlName="send_will_call_text"></ui-switch>
              </div>
            </div>
            <div class="form-group">
              <label class="col-sm-5 control-label text-left">Address</label>
              <div class="col-sm-7">
                <input class="form-control" type="text" formControlName="address">
              </div>
            </div>
            <div class="form-group">
              <label class="col-sm-5 control-label text-left">Tax ID</label>
              <div class="col-sm-7">
                <input class="form-control" type="text" formControlName="tax_id">
              </div>
            </div>
            <div class="form-group">
              <label class="col-sm-5 control-lable">Disabled PCS?</label>
              <div class="col-sm-7">
                <ui-switch id="pcs_disabled" name="pcs_disabled" formControlName="pcs_disabled"></ui-switch>
              </div>
            </div>
            @if (hospital.children.length === 0) {
              <div class="form-group">
                <label class="col-sm-5 control-lable">Advanced Ride Cancelation?</label>
                <div class="col-sm-7">
                  <ui-switch id="advanced_cancelation_enabled" name="advanced_cancelation_enabled" formControlName="advanced_cancelation_enabled">
                  </ui-switch>
                </div>
              </div>
            }
            @if (hospitalEditForm.get('advanced_cancelation_enabled') && hospitalEditForm.get('advanced_cancelation_enabled').value) {
              <div class="form-group">
                <label class="col-sm-5 control-label text-left">Ride Cancel Threshold</label>
                <div class="col-sm-7">
                  <input class="form-control" type="number" formControlName="advanced_cancelation_threshold">
                  @if (hospitalEditForm.get('advanced_cancelation_threshold').invalid &&
                    hospitalEditForm.get('advanced_cancelation_threshold').errors.required) {
                    <div class="form-error"
                      >
                      <p>Ride cancelation threshold cannot be empty.</p>
                    </div>
                  }
                  @if (hospitalEditForm.get('advanced_cancelation_threshold').invalid &&
                    !hospitalEditForm.get('advanced_cancelation_threshold').errors.required) {
                    <div class="form-error"
                      >
                      <p>Ride cancelation threshold should be in between 1 and 72 hours.</p>
                    </div>
                  }
                  <p class="small">
                    Please enter a value in hours.
                  </p>
                </div>
              </div>
            }
            @if (hospitalEditForm.get('advanced_cancelation_enabled') && hospitalEditForm.get('advanced_cancelation_enabled').value) {
              <div class="form-group">
                <label class="col-sm-5 control-label text-left">Advanced Ride Cancelation Reason</label>
                <div class="col-sm-7">
                  <input class="form-control" type="text" formControlName="advanced_cancelation_reason">
                  @if (hospitalEditForm.get('advanced_cancelation_reason').touched &&
                    hospitalEditForm.get('advanced_cancelation_reason').invalid) {
                    <div class="form-error"
                      >
                      <p>Advanced ride cancelation reason cannot be empty.</p>
                    </div>
                  }
                </div>
              </div>
            }
            @if (hospital.children.length === 0) {
              <div class="form-group">
                <label class="col-sm-5 control-lable">Advanced Calling Transport ZD Ticket</label>
                <div class="col-sm-7">
                  <ui-switch id="calling_providers_ticket_enabled" name="calling_providers_ticket_enabled" formControlName="calling_providers_ticket_enabled">
                  </ui-switch>
                </div>
              </div>
            }
            @if (hospitalEditForm.get('calling_providers_ticket_enabled').value && hospital.children.length == 0) {
              <div class="form-group">
                <label class="col-sm-5 control-label text-left">Trigger before ride start time</label>
                <div class="col-sm-7">
                  <input class="form-control" type="number" formControlName="calling_providers_ticket_threshold">
                  @if (hospitalEditForm.get('calling_providers_ticket_threshold').invalid) {
                    <div>
                      @if (hospitalEditForm.get('calling_providers_ticket_threshold').errors.required) {
                        <div class="form-error"
                          >
                          <p>Trigger before ride start time cannot be empty.</p>
                        </div>
                      }
                      @if (!hospitalEditForm.get('calling_providers_ticket_threshold').errors.required) {
                        <div class="form-error"
                          >
                          @if (hospitalEditForm.get('calling_providers_ticket_threshold').errors?.min) {
                            <p>
                              Trigger before ride start time should be greater than or equal to 0 hours.
                            </p>
                          }
                          @if (hospitalEditForm.get('calling_providers_ticket_threshold').errors?.max) {
                            <p>
                              Trigger before ride start time should be less than or equal to 96 hours.
                            </p>
                          }
                        </div>
                      }
                    </div>
                  }
                  <p class="small">
                    Please enter a value in hours.
                  </p>
                </div>
              </div>
            }
            <div class="form-group">
              <label class="col-sm-5 control-lable">
                Authorizations not by Roundtrip
                <small class="display-block">(Redox-integrated)</small>
              </label>
              <div class="col-sm-7">
                <ui-switch id="auth_disabled"
                  name="auth_disabled"
                  formControlName="auth_disabled">
                </ui-switch>
              </div>
            </div>
            <div class="form-group">
              <label class="col-sm-5 control-lable">Allow Rider Creation</label>
              <div class="col-sm-7">
                <ui-switch id="allow_rider_creation"
                  name="allow_rider_creation"
                  formControlName="allow_rider_creation">
                </ui-switch>
              </div>
            </div>
            <div class="action-wrap text-left">
              <button type="button" class="btn btn-default" (click)="cancelEditing()">Close</button>
              <button type="button" class="btn btn-primary" [disabled]="!hospitalEditForm.valid" (click)="update()">Update</button>
            </div>
          </form>
        </div>
      </div>
    }

  </tab>

  <tab (select)="tabChanged($event)">
    <ng-template tabHeading>
      Vehicle Rates
    </ng-template>

    <app-vehicle-rates [hospital]="hospital" (vehicleRatesUpdated)="vehicleRatesUpdate($event)"></app-vehicle-rates>
  </tab>

  <tab (select)="tabChanged($event)">
    <ng-template tabHeading>
      Network
    </ng-template>

    <ul class="list-group">
      @for (h of hospitals; track h) {
        <li class="list-group-item" [ngClass]="{'active': isHospitalChild(h)}" (click)="toggleHospitalChild(h)"
          >
          @if (isHospitalChild(h)) {
            <span class="badge">
              <i class="fa fa-check"></i>
            </span>
          }
          {{h.name}}
        </li>
      }
    </ul>
  </tab>

  <tab (select)="tabChanged($event)">
    <ng-template tabHeading>
      Programs
    </ng-template>

    <tabset>
      <tab heading="Active Programs" (select)="tabChanged($event)">
        @if (!hospitalPrograms.length) {
          <ul class="list-group">
            No Attached Programs Found
          </ul>
        }
        @if (hospitalPrograms.length) {
          <ul class="list-group">
            @for (program of hospitalPrograms; track program) {
              <li class="list-group-item" (click)="updateProgram(program)">
                {{ program.name }}
              </li>
            }
          </ul>
        }
        <button class="btn btn-primary" (click)="addProgram()">
          <app-icon icon="book" iconClass="button-icon"></app-icon> Add Program
        </button>
      </tab>
      <tab heading="Inactive Programs" (select)="tabChanged($event)">
        @if (!deactivatedHospitalPrograms.length) {
          <ul class="list-group">
            No Inactive Programs Found
          </ul>
        }
        @if (deactivatedHospitalPrograms.length) {
          <ul class="list-group">
            @for (program of deactivatedHospitalPrograms; track program) {
              <li class="list-group-item d-flex justify-content-between align-items-center">
                {{ program.name }}
                <button class="btn btn-secondary btn-sm" (click)="restoreProgram(program)">Restore</button>
              </li>
            }
          </ul>
        }
      </tab>
    </tabset>
  </tab>

  <tab>
    <ng-template tabHeading>
      Locations
    </ng-template>

    @if (!hospitalLocations.length) {
      <ul class="list-group">
        No Attached Locations Found
      </ul>
    }

    @if (hospitalLocations && hospitalLocations.length) {
      <p-table
        [value]="hospitalLocations"
        [totalRecords]="totalRecords"
        [paginator]="true"
        [rows]="rows"
        [first]="first"
        [showCurrentPageReport]="true"
        selectionMode="single"
        [(selection)]="selectedHospitalLocation"
        (onRowSelect)="addLocation($event)"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
        [rowsPerPageOptions]="[10, 25, 50]"
        stateStorage="session"
        stateKey="state-session-hospital-locations"
        >
        <ng-template pTemplate="header">
          <tr>
            @for (key of keys; track key) {
              <th pSortableColumn="{{ key }}">
                {{ locationTableConfig?.columns[key]?.title }} <p-sortIcon field="{{ key }}"></p-sortIcon>
              </th>
            }
          </tr>
          <tr>
            <th style="width:25%">
              <p-columnFilter
                type="text"
                field="id"
                [placeholder]="placeholders['id']"
                ariaLabel="Filter id"
                [showMenu]="false"
                [showClearButton]="false"
                />
            </th>
            <th style="width:25%">
              <p-columnFilter
                type="text"
                field="name"
                [placeholder]="placeholders['name']"
                ariaLabel="Filter name"
                [showMenu]="false"
                [showClearButton]="false"
                />
            </th>
            <th style="width:25%">
              <p-columnFilter
                type="text"
                field="zone_name"
                [placeholder]="placeholders['zone_name']"
                ariaLabel="Filter venue name"
                [showMenu]="false"
                [showClearButton]="false"
                />
            </th>
            <th style="width:25%">
              <p-columnFilter
                type="text"
                field="created_on"
                [placeholder]="placeholders['created_on']"
                ariaLabel="Filter created on"
                [showMenu]="false"
                [showClearButton]="false"
                />
            </th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-hospitalLocation>
          <tr [pSelectableRow]="hospitalLocation">
            @for (key of keys; track key) {
              <td>{{ hospitalLocation?.[key] }}</td>
            }
          </tr>
        </ng-template>
      </p-table>
    }

    <button class="btn btn-primary" (click)="addLocation($event)">
      <app-icon icon="book" iconClass="button-icon"></app-icon> Add Locations
    </button>
  </tab>

  <tab>
    <ng-template tabHeading>
      TigerConnect Integration
    </ng-template>
    <app-tiger-connect-integration [hospital]="hospital"></app-tiger-connect-integration>
  </tab>

  <tab (select)="tabChanged($event)">
    <ng-template tabHeading>
      Community Settings
    </ng-template>

    <app-community-settings></app-community-settings>
  </tab>

  <tab (select)="tabChanged($event)">
    <ng-template tabHeading>
      Geofence
    </ng-template>

    <app-geofence [geofence]="geofence" [hospitalId]="hospital.id"></app-geofence>
  </tab>

  <tab (select)="tabChanged($event)">
    <ng-template tabHeading>
      User Upload
    </ng-template>

    <app-user-upload [hospitalId]="hospital.id"></app-user-upload>
  </tab>

  <tab (select)="tabChanged($event)">
    <ng-template tabHeading>
      Integrations
    </ng-template>

    <app-integrations [hospital]="hospital"></app-integrations>
  </tab>
  <tab (select)="tabChanged($event)">
    <ng-template tabHeading>
      Benefit Management
    </ng-template>

    <app-benefit-management [hospital]="hospital"></app-benefit-management>
  </tab>
  @if (hospital.parent_id === null) {
    <tab (select)="tabChanged($event)">
      <ng-template tabHeading>
        Quicksight Reports
      </ng-template>
      <app-quicksight-reports [hospital]="hospital"></app-quicksight-reports>
    </tab>
  }
</tabset>
